<template>
    <div class="login-page">
        <header class="login-nav">
            <div class="nav-brand">
                <BrandLogoIcon :size="28" />
                <span class="nav-brand-name">{{ $t('monitorConsole.title') }}</span>
            </div>
            <div class="nav-actions">
                <LanguageToggle />
                <ThemeToggle />
                <FullscreenToggle />
            </div>
        </header>

        <main class="login-hero">
            <div class="hero-copy">
                <h1 class="display-xl">{{ $t('monitorConsole.title') }}</h1>
                <p class="hero-subtitle">{{ $t('login.title') }}</p>
            </div>

            <div class="login-panel mc-form-controls">
            <!-- 登录/注册切换标签 -->
            <el-tabs v-model="activeTab" class="login-tabs" @tab-change="handleTabChange">
                <el-tab-pane :label="$t('login.loginButton')" name="login">
                    <!-- 登录表单 -->
                    <div class="form-container">
                        <!-- MFA验证界面 -->
                        <div v-if="showMFA" class="mfa-container">
                            <div class="mfa-header">
                                <h3>{{ $t('mfa.title') }}</h3>
                                <p>{{ $t('mfa.description') }}</p>
                            </div>
                            
                            <el-form @submit.prevent>
                                <el-form-item :error="mfaError">
                                    <el-input
                                        v-model="mfaCode"
                                        :placeholder="$t('mfa.placeholder')"
                                        maxlength="6"
                                        class="mfa-input"
                                        @input="handleMFAInput"
                                    >
                                        <template #prefix>
                                            <el-icon>
                                                <Key/>
                                            </el-icon>
                                        </template>
                                    </el-input>
                                </el-form-item>
                                
                                <el-form-item>
                                    <el-button
                                        type="primary"
                                        class="w-100"
                                        :loading="loading"
                                        :disabled="mfaCode.length !== 6"
                                        @click="verifyMFA"
                                    >
                                        {{ $t('mfa.verifyButton') }}
                                    </el-button>
                                </el-form-item>
                                
                                <el-form-item>
                                    <el-button class="w-100" @click="cancelMFA">
                                        {{ $t('mfa.backButton') }}
                                    </el-button>
                                </el-form-item>
                            </el-form>
                        </div>
                        
                        <!-- 密码登录表单 -->
                        <el-form
                            v-else-if="loginMethod === 'password'"
                            ref="passwordLoginFormRef"
                            :model="loginForm"
                            @submit.prevent="handlePasswordLogin"
                            size="large"
                        >
                            <el-form-item>
                                <el-input
                                    v-model="loginForm.username"
                                    :placeholder="$t('login.usernamePlaceholder')"
                                    autocomplete="username"
                                >
                                    <template #prefix>
                                        <el-icon>
                                            <User/>
                                        </el-icon>
                                    </template>
                                </el-input>
                            </el-form-item>
                            
                            <el-form-item>
                                <el-input
                                    v-model="loginForm.password"
                                    type="password"
                                    :placeholder="$t('login.passwordPlaceholder')"
                                    show-password
                                    autocomplete="current-password"
                                    @input="checkPasswordStrength"
                                >
                                    <template #prefix>
                                        <el-icon>
                                            <Lock/>
                                        </el-icon>
                                    </template>
                                </el-input>
                                <!-- 密码强度指示器 -->
                                <div v-if="passwordStrength.score > 0" class="password-strength-meter">
                                    <el-progress
                                        :percentage="(passwordStrength.score + 1) * 20"
                                        :color="getPasswordStrengthColor(passwordStrength.score)"
                                        :show-text="false"
                                        :stroke-width="6"
                                    />
                                    <div class="strength-info mt-1">
								<span class="strength-label">
                      {{ $t('login.passwordStrength') }}: {{ getPasswordStrengthLabel(passwordStrength.score) }}
								</span>
                                    </div>
                                </div>
                            </el-form-item>
                            
                            <!-- 防暴力破解验证码 -->
                            <el-form-item v-if="showCaptcha">
                                <div class="captcha-wrapper">
                                    <CaptchaComponent ref="loginCaptchaRef" v-model="loginCaptcha"
                                                      :placeholder="$t('login.captchaPlaceholder')"
                                                      @refresh="onLoginCaptchaRefresh"/>
                                </div>
                            </el-form-item>
                            
                            <el-form-item>
                                <div class="flex justify-between w-100">
                                    <el-checkbox v-model="rememberMe" @change="onRememberMeChange">
                                        {{ $t('login.rememberMe') }}
                                    </el-checkbox>
                                    <el-button type="primary" link @click="handleForgotPassword">
                                        {{ $t('login.forgotPassword') }}
                                    </el-button>
                                </div>
                            </el-form-item>
                            
                            <el-form-item>
                                <el-button type="primary" class="w-100" :loading="loading" @click="handlePasswordLogin">
                                    {{ $t('login.loginButton') }}
                                </el-button>
                            </el-form-item>

                            <div class="alternate-login">
                                <button type="button" class="alternate-link" @click="switchLoginMethod('sms')">
                                    {{ $t('login.useSmsLogin') }}
                                </button>
                            </div>
                            
                            <div class="security-tips">
                                <h4>{{ $t('securityTips.title') }}</h4>
                                <ul>
                                    <li>{{ $t('securityTips.tip1') }}</li>
                                    <li>{{ $t('securityTips.tip2') }}</li>
                                    <li>{{ $t('securityTips.tip3') }}</li>
                                </ul>
                            </div>
                        </el-form>
                        
                        <!-- 短信验证登录表单 -->
                        <el-form
                            v-else-if="loginMethod === 'sms'"
                            :model="smsLoginForm"
                            @submit.prevent="handleSmsLogin"
                            size="large"
                        >
                            <el-form-item>
                                <el-input
                                    v-model="smsLoginForm.phone"
                                    :placeholder="$t('login.usernamePlaceholder')"
                                    autocomplete="tel"
                                >
                                    <template #prefix>
                                        <el-icon>
                                            <Iphone/>
                                        </el-icon>
                                    </template>
                                </el-input>
                            </el-form-item>
                            
                            <el-form-item>
                                <div class="sms-wrapper flex gap-2">
                                    <el-input
                                        v-model="smsLoginForm.code"
                                        :placeholder="$t('login.verificationCodePlaceholder')"
                                        autocomplete="off"
                                        class="flex-1"
                                    >
                                        <template #prefix>
                                            <el-icon>
                                                <Message/>
                                            </el-icon>
                                        </template>
                                    </el-input>
                                    <el-button
                                        type="primary"
                                        plain
                                        :disabled="smsCodeTimer > 0 || !smsLoginForm || !validatePhone(smsLoginForm.phone)"
                                        @click="sendSmsCode"
                                    >
                                        {{
                                            smsCodeTimer > 0 ? $t('login.resendCode', {seconds: smsCodeTimer}) : $t('login.sendCode')
                                        }}
                                    </el-button>
                                </div>
                            </el-form-item>
                            
                            <el-form-item>
                                <el-button type="primary" class="w-100" :loading="loading" @click="handleSmsLogin">
                                    {{ $t('login.loginButton') }}
                                </el-button>
                            </el-form-item>

                            <div class="alternate-login">
                                <button type="button" class="alternate-link" @click="switchLoginMethod('password')">
                                    {{ $t('login.usePasswordLogin') }}
                                </button>
                            </div>
                        </el-form>
                    </div>
                </el-tab-pane>
                
                <el-tab-pane :label="$t('login.registerButton')" name="register">
                    <!-- 注册表单 -->
                    <div class="form-container">
                        <!-- 注册方式切换 -->
                        <div class="register-methods mb-4">
                            <el-radio-group v-model="registerMethod" @change="switchRegisterMethod"
                                            class="method-switch">
                                <el-radio-button value="phone">{{ $t('tabs.phoneRegister') }}</el-radio-button>
                                <el-radio-button value="email">{{ $t('tabs.emailRegister') }}</el-radio-button>
                            </el-radio-group>
                        </div>
                        
                        <el-form
                            :model="registerForm"
                            @submit.prevent="handleRegister"
                            size="large"
                        >
                            <!-- 手机注册表单 -->
                            <template v-if="registerMethod === 'phone'">
                                <el-form-item>
                                    <el-input
                                        v-model="registerForm.username"
                                        :placeholder="$t('login.usernamePlaceholder')"
                                        autocomplete="username"
                                    >
                                        <template #prefix>
                                            <el-icon>
                                                <User/>
                                            </el-icon>
                                        </template>
                                    </el-input>
                                </el-form-item>
                                <el-form-item>
                                    <el-input
                                        v-model="registerForm.phone"
                                        :placeholder="$t('login.phonePlaceholder')"
                                        autocomplete="tel"
                                    >
                                        <template #prefix>
                                            <el-icon>
                                                <Iphone/>
                                            </el-icon>
                                        </template>
                                    </el-input>
                                </el-form-item>
                                
                                <el-form-item>
                                    <div class="sms-wrapper flex gap-2">
                                        <el-input
                                            v-model="registerForm.code"
                                            :placeholder="$t('login.verificationCodePlaceholder')"
                                            autocomplete="off"
                                            class="flex-1"
                                        >
                                            <template #prefix>
                                                <el-icon>
                                                    <Message/>
                                                </el-icon>
                                            </template>
                                        </el-input>
                                        <el-button
                                            type="primary"
                                            plain
                                            :disabled="smsCodeTimer > 0 || !registerForm || !validatePhone(registerForm.phone)"
                                            @click="sendSmsCode"
                                        >
                                            {{
                                                smsCodeTimer > 0 ? $t('login.resendCode', {seconds: smsCodeTimer}) : $t('login.sendCode')
                                            }}
                                        </el-button>
                                    </div>
                                </el-form-item>
                            </template>
                            
                            <!-- 邮箱注册表单 -->
                            <template v-else-if="registerMethod === 'email'">
                                <el-form-item>
                                    <el-input
                                        v-model="registerForm.email"
                                        :placeholder="$t('login.emailPlaceholder')"
                                        autocomplete="email"
                                    >
                                        <template #prefix>
                                            <el-icon>
                                                <Message/>
                                            </el-icon>
                                        </template>
                                    </el-input>
                                </el-form-item>
                                
                                <el-form-item>
                                    <div class="sms-wrapper flex gap-2">
                                        <el-input
                                            v-model="registerForm.emailCode"
                                            :placeholder="$t('login.verificationCodePlaceholder')"
                                            autocomplete="off"
                                            class="flex-1"
                                        >
                                            <template #prefix>
                                                <el-icon>
                                                    <Message/>
                                                </el-icon>
                                            </template>
                                        </el-input>
                                        <el-button
                                            type="primary"
                                            plain
                                            :disabled="emailCodeTimer > 0 || !registerForm || !isValidEmail(registerForm.email)"
                                            @click="sendEmailCode"
                                        >
                                            {{
                                                emailCodeTimer > 0 ? $t('login.resendCode', {seconds: emailCodeTimer}) : $t('login.sendCode')
                                            }}
                                        </el-button>
                                    </div>
                                </el-form-item>
                            </template>
                            
                            <el-form-item>
                                <el-input
                                    v-model="registerForm.password"
                                    type="password"
                                    :placeholder="$t('login.passwordPlaceholder')"
                                    show-password
                                    autocomplete="new-password"
                                    @input="checkRegisterPasswordStrength"
                                >
                                    <template #prefix>
                                        <el-icon>
                                            <Lock/>
                                        </el-icon>
                                    </template>
                                </el-input>
                                <!-- 密码强度指示器 -->
                                <div v-if="registerPasswordStrength.score > 0" class="password-strength-meter">
                                    <el-progress
                                        :percentage="(registerPasswordStrength.score + 1) * 20"
                                        :color="getPasswordStrengthColor(registerPasswordStrength.score)"
                                        :show-text="false"
                                        :stroke-width="6"
                                    />
                                    <div class="strength-info mt-1">
								<span class="strength-label">
                      {{ $t('login.passwordStrength') }}: {{ getPasswordStrengthLabel(registerPasswordStrength.score) }}
								</span>
                                    </div>
                                    <!-- 密码强度反馈 -->
                                    <ul v-if="registerPasswordStrength.feedback.length > 0" class="strength-feedback">
                                        <li v-for="(feedback, index) in registerPasswordStrength.feedback" :key="index"
                                            class="feedback-item">
                                            {{ feedback }}
                                        </li>
                                    </ul>
                                </div>
                            </el-form-item>
                            
                            <el-form-item>
                                <el-input
                                    v-model="registerForm.confirmPassword"
                                    type="password"
                                    :placeholder="$t('login.confirmPasswordPlaceholder')"
                                    show-password
                                    autocomplete="new-password"
                                >
                                    <template #prefix>
                                        <el-icon>
                                            <Lock/>
                                        </el-icon>
                                    </template>
                                </el-input>
                            </el-form-item>
                            
                            <el-form-item>
                                <el-button type="primary" class="w-100" :loading="loading"
                                           @click="handleRegister">
                                    {{ $t('login.registerButton') }}
                                </el-button>
                            </el-form-item>
                            
                            <div class="security-tips">
                                <h4>{{ $t('passwordRequirements.title') }}</h4>
                                <ul>
                                    <li>{{
                                            $t('passwordRequirements.length', {length: (registerForm && registerForm.password) ? registerForm.password.length : 0})
                                        }}
                                    </li>
                                    <li>{{ $t('passwordRequirements.uppercase') }}</li>
                                    <li>{{ $t('passwordRequirements.lowercase') }}</li>
                                    <li>{{ $t('passwordRequirements.number') }}</li>
                                    <li>{{ $t('passwordRequirements.special') }}</li>
                                    <li>{{ $t('passwordRequirements.noCommonPattern') }}</li>
                                </ul>
                            </div>
                        </el-form>
                    </div>
                </el-tab-pane>
            </el-tabs>
            </div>
        </main>

        <footer class="login-footer">
            <div class="footer-inner">
                <span class="footer-meta">
                    <span class="footer-brand">{{ $t('monitorConsole.title') }}</span>
                    <span class="footer-version">{{ appVersionLabel }}</span>
                </span>
            </div>
        </footer>
    </div>
</template>

<script>
import {AUTH_API} from '@config/api';
import { httpFetch } from '@/utils/httpClient';
import PasswordValidator from '@/utils/login/passwordValidator';
import rateLimiter from '@/utils/login/rateLimiter';
import sessionManager from '@/utils/login/sessionManager';
import CryptoJS from 'crypto-js';
import CaptchaComponent from './CaptchaComponent.vue';
import BrandLogoIcon from './BrandLogoIcon.vue';
import LanguageToggle from './LanguageToggle.vue';
import ThemeToggle from './ThemeToggle.vue';
import FullscreenToggle from './FullscreenToggle.vue';
import {User, Key, Iphone, Message, Lock} from '@element-plus/icons-vue';
import {ElMessage} from 'element-plus';
import { getAppVersionLabel } from '@/config/version';

// SHA256加密函数
function sha256(message) {
    return CryptoJS.SHA256(message).toString();
}

const REMEMBER_ME_KEY = 'mc_login_remember_me'
const REMEMBER_USERNAME_KEY = 'mc_login_remember_username'
const REMEMBER_PASSWORD_KEY = 'mc_login_remember_password'
/** Local obfuscation only — not a substitute for proper secret storage */
const REMEMBER_SECRET = 'MonitorCenter.RememberCredentials.v1'

function encodeRememberPassword(plain) {
    return CryptoJS.AES.encrypt(String(plain ?? ''), REMEMBER_SECRET).toString()
}

function decodeRememberPassword(cipher) {
    try {
        const bytes = CryptoJS.AES.decrypt(String(cipher || ''), REMEMBER_SECRET)
        return bytes.toString(CryptoJS.enc.Utf8) || ''
    } catch {
        return ''
    }
}

export default {
    name: 'LoginForm',
    components: {
        CaptchaComponent,
        BrandLogoIcon,
        LanguageToggle,
        ThemeToggle,
        FullscreenToggle,
        User,
        Key,
        Iphone,
        Message,
        Lock
    },
    emits: ['switch-to-email-login'],
    data() {
        return {
            appVersionLabel: getAppVersionLabel(),
            activeTab: 'login', // 控制el-tabs的激活项
            loginMethod: 'password', // 'password', 'sms'
            registerMethod: 'phone', // 'phone', 'email'
            loading: false, // 提交状态
            loginForm: {
                username: '',
                password: ''
            },
            smsLoginForm: {
                phone: '',
                code: ''
            },
            emailLoginForm: {
                email: '',
                code: ''
            },
            registerForm: {
                username: '',
                phone: '',
                code: '',
                email: '',
                emailCode: '',
                password: '',
                confirmPassword: ''
            },
            smsCodeTimer: 0,
            emailCodeTimer: 0,
            loginCaptcha: '', // 用户输入的登录验证码
            showCaptcha: false, // 是否显示登录验证码
            loginAttempts: 0, // 总登录尝试次数
            failedLoginAttempts: 0, // 连续失败次数
            rememberMe: false, // 是否记住我
            lastFailedAttemptTime: null, // 上次失败时间
            lockoutUntil: null, // 解锁时间
            passwordStrength: {score: 0, feedback: []}, // 登录密码强度
            registerPasswordStrength: {score: 0, feedback: []}, // 注册密码强度
            showMFA: false, // 是否显示MFA界面
            mfaCode: '', // MFA验证码
            mfaError: '' // MFA错误信息
        }
    },
    computed: {
        isRegister() {
            return this.activeTab === 'register';
        },
        isLockedOut() {
            if (!this.lockoutUntil) return false;
            return Date.now() < this.lockoutUntil;
        },
        lockoutMinutes() {
            if (!this.lockoutUntil) return 0;
            const minutes = Math.ceil((this.lockoutUntil - Date.now()) / 60000);
            return minutes > 0 ? minutes : 0;
        },
        isPasswordValid() {
            return this.passwordStrength.isValid || this.passwordStrength.score === 0;
        },
        isRegisterPasswordValid() {
            return this.registerPasswordStrength.isValid;
        }
    },
    async mounted() {
        // 加载限流数据
        rateLimiter.loadFromStorage();

        this.loadRememberMe();
        
        // 检查是否已有有效会话
        this.checkExistingSession();
    },
    methods: {
        loadRememberMe() {
            try {
                const remember = localStorage.getItem(REMEMBER_ME_KEY) === '1'
                this.rememberMe = remember
                if (!remember) return

                const username = localStorage.getItem(REMEMBER_USERNAME_KEY)
                if (username) {
                    this.loginForm.username = username
                }
                const cipher = localStorage.getItem(REMEMBER_PASSWORD_KEY)
                if (cipher) {
                    this.loginForm.password = decodeRememberPassword(cipher)
                }
            } catch {
                // ignore storage errors
            }
        },

        saveRememberMe() {
            try {
                const username = this.loginForm.username?.trim()
                if (this.rememberMe && username) {
                    localStorage.setItem(REMEMBER_ME_KEY, '1')
                    localStorage.setItem(REMEMBER_USERNAME_KEY, username)
                    localStorage.setItem(
                        REMEMBER_PASSWORD_KEY,
                        encodeRememberPassword(this.loginForm.password || '')
                    )
                    return
                }

                localStorage.removeItem(REMEMBER_ME_KEY)
                localStorage.removeItem(REMEMBER_USERNAME_KEY)
                localStorage.removeItem(REMEMBER_PASSWORD_KEY)
            } catch {
                // ignore storage errors
            }
        },

        onRememberMeChange(checked) {
            // Unchecking should clear stored credentials immediately
            if (!checked) {
                try {
                    localStorage.removeItem(REMEMBER_ME_KEY)
                    localStorage.removeItem(REMEMBER_USERNAME_KEY)
                    localStorage.removeItem(REMEMBER_PASSWORD_KEY)
                } catch {
                    // ignore
                }
            }
        },

        // 检查现有会话
        async checkExistingSession() {
            if (sessionManager.getSessionId()) {
                this.$router.push('/monitor');
            }
        },
        
        handleTabChange() {
            this.showMFA = false;
            this.loginMethod = 'password';
        },
        
        switchToLogin() {
            this.activeTab = 'login';
        },
        switchToRegister() {
            this.activeTab = 'register';
        },
        switchLoginMethod(method) {
            this.loginMethod = method;
            this.showMFA = false; // 切换登录方式时隐藏MFA
        },
        switchRegisterMethod(method) {
            this.registerMethod = method;
            // 切换注册方式时清空表单
            if (method === 'phone') {
                this.registerForm.email = '';
                this.registerForm.emailCode = '';
            } else {
                this.registerForm.phone = '';
                this.registerForm.code = '';
            }
        },
        // 邮箱格式验证
        isValidEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        },
        // 检查密码强度
        checkPasswordStrength() {
            this.passwordStrength = PasswordValidator.validate(this.loginForm.password);
        },
        // 检查注册密码强度
        checkRegisterPasswordStrength() {
            this.registerPasswordStrength = PasswordValidator.validate(this.registerForm.password);
        },
        // 获取密码强度标签
        getPasswordStrengthLabel(score) {
            return PasswordValidator.getStrengthLabel(score);
        },
        // 获取密码强度颜色
        getPasswordStrengthColor(score) {
            return PasswordValidator.getStrengthColor(score);
        },
        // 处理MFA输入
        handleMFAInput() {
            this.mfaCode = this.mfaCode.replace(/\D/g, ''); // 只允许数字
            this.mfaError = ''; // 清除错误信息
        },
        // 验证MFA
        async verifyMFA() {
            if (this.mfaCode.length !== 6) {
                this.mfaError = '请输入6位验证码';
                return;
            }
            
            try {
                this.loading = true;
                
                console.log('调用MFA验证API:', AUTH_API.VERIFY_MFA);
                console.log('请求参数:', {code: this.mfaCode});
                
                // 模拟MFA验证...
                ElMessage.success('MFA验证成功');
                this.showMFA = false;
                this.mfaCode = '';
            } catch (error) {
                console.error('MFA验证失败:', error);
                this.mfaError = '验证失败，请稍后重试';
            } finally {
                this.loading = false;
            }
        },
        // 取消MFA验证
        cancelMFA() {
            this.showMFA = false;
            this.mfaCode = '';
            this.mfaError = '';
        },
        async handlePasswordLogin() {
            // 验证输入字段
            if (!this.loginForm.username || !this.loginForm.username.trim()) {
                ElMessage.error('请输入用户名');
                return;
            }
            
            // 检查限流
            const rateLimitCheck = rateLimiter.check(this.loginForm.username);
            if (!rateLimitCheck.allowed) {
                const minutes = Math.ceil(rateLimitCheck.retryAfter / 60);
                ElMessage.error(`账户已被锁定，请${minutes}分钟后再试`);
                return;
            }
            
            // 如果启用了验证码，则验证
            if (this.showCaptcha && !this.validateLoginCaptcha()) {
                this.handleFailedLogin('验证码错误');
                return;
            }
            
            try {
                this.loading = true;
                
                // 对密码进行SHA256加密，如果密码为空则使用空字符串
                const passwordToHash = this.loginForm.password || '';
                const hashedPassword = sha256(passwordToHash);
                const loginData = {
                    phone: this.loginForm.username,
                    password: hashedPassword,
                };
                
                console.log('调用密码登录API:', AUTH_API.LOGIN_PASSWORD);
                console.log('请求参数:', loginData);
                
                const response = await httpFetch(AUTH_API.LOGIN_PASSWORD, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(loginData)
                });
                const data = await response.json();
                
                console.log('响应数据:', data);
                
                if (data.code === 200) {
                  // Calculate privileges if missing
                    let privileges = data.data.privileges;
                    const roles = data.data.roles || data.data.role;
                    const isAdmin = data.data.isAdmin;

                    if (privileges === undefined || privileges === null) {
                        if (isAdmin === true || isAdmin === 1 || isAdmin === '1') {
                            privileges = 0;
                        } else if (roles) {
                             const roleStr = String(roles).toLowerCase();
                             if (roleStr === 'admin' || roleStr.includes('admin')) {
                                 privileges = 0;
                             }
                        }
                    }

                    await sessionManager.setSession({
                        user: {
                            id: data.data.id,
                            username: data.data.name,
                            privileges: privileges,
                            roles: roles
                        },
                        sessionId: data.data.sessionId,
                        token: data.data.token || data.data.sessionId,
                    });
                    
                    // 检查密码是否为空，如果为空则跳转到密码设置页面
                    if (!this.loginForm.password) {
                        ElMessage.warning('请联系管理员设置密码');
                    } else {
                        this.handleSuccessfulLogin();
                        this.$router.push('/monitor');
                    }
                } else if (data.code === 401 && (data.message === 'PASSWORD_RESET_REQUIRED' || data.message === '用户首次登录需要设置密码')) {
                    ElMessage.warning('用户首次登录需要设置密码，请联系管理员');
                } else {
                    this.handleFailedLogin(data.message || '登录失败');
                }
            } catch (error) {
                console.error('密码登录失败:', error);
                this.handleFailedLogin('登录失败，请稍后重试');
            } finally {
                this.loading = false;
            }
        },
        handleSuccessfulLogin() {
            this.saveRememberMe();
            rateLimiter.reset(this.loginForm.username);
            this.failedLoginAttempts = 0;
            this.lastFailedAttemptTime = null;
            this.lockoutUntil = null;
            this.showCaptcha = false;
            this.loginCaptcha = '';
            this.passwordStrength = {score: 0, feedback: []};
            ElMessage.success('登录成功');
        },
        handleFailedLogin(message = '登录失败') {
            rateLimiter.recordFailure(this.loginForm.username);
            
            this.loginAttempts++;
            this.failedLoginAttempts++;
            this.lastFailedAttemptTime = Date.now();
            
            const remainingAttempts = rateLimiter.getRemainingAttempts(this.loginForm.username);
            if (remainingAttempts <= 2) {
                this.showCaptcha = true;
            }
            
            // 将'未查询到用户'错误信息替换为'账户或密码错误'
            const displayMessage = message === '未查询到用户' ? '账户或密码错误' : message;
            
            const rateLimitCheck = rateLimiter.check(this.loginForm.username);
            if (!rateLimitCheck.allowed) {
                const minutes = Math.ceil(rateLimitCheck.retryAfter / 60);
                ElMessage.error(`${displayMessage}。账户已被锁定，请${minutes}分钟后再试`);
            } else {
                ElMessage.error(displayMessage);
            }
            
            this.refreshLoginCaptcha();
        },
        async handleSmsLogin() {
            try {
                this.loading = true;
                console.log('调用短信登录API:', AUTH_API.LOGIN_SMS);
                console.log('请求参数:', this.smsLoginForm);
                
                const response = await httpFetch(AUTH_API.LOGIN_SMS, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(this.smsLoginForm)
                });
                const data = await response.json();
                
                if (data.code === 200 || data.code === '200') {
                    // Calculate privileges if missing
                    let privileges = data.data.privileges;
                    const roles = data.data.roles || data.data.role;
                    const isAdmin = data.data.isAdmin;

                    if (privileges === undefined || privileges === null) {
                         if (isAdmin === true || isAdmin === 1 || isAdmin === '1') {
                            privileges = 0;
                        } else if (roles) {
                             const roleStr = String(roles).toLowerCase();
                             if (roleStr === 'admin' || roleStr.includes('admin')) {
                                 privileges = 0;
                             }
                        }
                    }

                    await sessionManager.setSession({
                        user: {
                            id: data.data.id,
                            username: data.data.name,
                            privileges: privileges,
                            roles: roles
                        },
                        sessionId: data.data.sessionId,
                        token: data.data.token || data.data.sessionId,
                    });
                    
                    this.handleSuccessfulLogin();
                    this.$router.push('/monitor');
                } else {
                    this.handleFailedLogin(data.message || '登录失败');
                }
            } catch (error) {
                console.error('短信登录失败:', error);
                ElMessage.error('登录失败，请稍后重试');
            } finally {
                this.loading = false;
            }
        },
        async handleRegister() {
            if (this.registerForm.password !== this.registerForm.confirmPassword) {
                ElMessage.warning('两次输入的密码不一致');
                return
            }
            
            if (!this.isRegisterPasswordValid) {
                ElMessage.warning('密码强度不足，请按照要求设置密码');
                return;
            }
            
            // 验证手机注册
            if (this.registerMethod === 'phone') {
                if (!this.registerForm.username || !this.registerForm.username.trim()) {
                    ElMessage.warning(this.$t('errors.usernameRequired'));
                    return;
                }
                if (!this.registerForm.phone || !this.registerForm.phone.trim()) {
                    ElMessage.warning(this.$t('errors.phoneRequired'));
                    return;
                }
                if (!this.validatePhone(this.registerForm.phone)) {
                    ElMessage.warning(this.$t('errors.invalidPhone'));
                    return;
                }
                if (!this.registerForm.code) {
                    ElMessage.warning('请输入验证码');
                    return;
                }
            }
            
            // 验证邮箱注册
            if (this.registerMethod === 'email') {
                if (!this.isValidEmail(this.registerForm.email)) {
                    ElMessage.warning('请输入正确的邮箱地址');
                    return;
                }
                if (!this.registerForm.emailCode) {
                    ElMessage.warning('请输入验证码');
                    return;
                }
            }
            
            try {
                this.loading = true;
                
                // 对密码进行SHA256加密
                const hashedPassword = sha256(this.registerForm.password);
                const hashedConfirmPassword = sha256(this.registerForm.confirmPassword);
                
                // 构建注册数据
                const registerData = {
                    password: hashedPassword,
                    confirmPassword: hashedConfirmPassword,
                    method: this.registerMethod
                };
                
                if (this.registerMethod === 'phone') {
                    registerData.username = this.registerForm.username;
                    registerData.phone = this.registerForm.phone;
                    registerData.code = this.registerForm.code;
                } else {
                    registerData.email = this.registerForm.email;
                    registerData.code = this.registerForm.emailCode;
                }
                
                console.log('调用注册API:', AUTH_API.REGISTER);
                console.log('请求参数:', registerData);
                const response = await httpFetch(AUTH_API.REGISTER, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(registerData)
                });
                let data = null;
                try {
                    data = await response.json();
                } catch (e) {
                    console.warn('注册接口返回非JSON', e);
                }
                
                const success =
                    response.ok &&
                    (data?.code === 200 || data?.code === '200' || data?.success === true);
                
                if (success) {
                    ElMessage.success(data?.message || this.$t('login.registerSuccess') || '注册成功');
                    // 注册成功后回到登录页
                    this.switchToLogin();
                } else {
                    const message =
                        data?.message ||
                        (data?.code ? `注册失败（${data.code}）` : '') ||
                        '注册失败，请稍后重试';
                    ElMessage.error(message);
                }
            } catch (error) {
                console.error('注册失败:', error);
                ElMessage.error('注册失败，请稍后重试');
            } finally {
                this.loading = false;
            }
        },
        async sendSmsCode() {
            const phone = this.isRegister ? this.registerForm.phone : this.smsLoginForm.phone;
            if (!phone || !phone.trim()) {
                ElMessage.warning(this.$t('errors.phoneRequired'));
                return;
            }
            if (!this.validatePhone(phone)) {
                ElMessage.warning(this.$t('errors.invalidPhone'));
                return;
            }
            
            try {
                console.log('调用发送短信验证码API:', AUTH_API.SEND_SMS_CODE);
                console.log('请求参数:', {phone});
                
                const response = await httpFetch(AUTH_API.SEND_SMS_CODE, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({phone})
                });
                const data = await response.json();
                
                if (data.code === '200' || data.code === 200) {
                    this.startSmsTimer();
                    ElMessage.success('验证码已发送');
                } else {
                    ElMessage.error(data.message || '发送验证码失败');
                }
            } catch (error) {
                console.error('发送验证码失败:', error);
                ElMessage.error('发送验证码失败，请稍后重试');
            }
        },
        startSmsTimer() {
            this.smsCodeTimer = 60;
            const timer = setInterval(() => {
                this.smsCodeTimer--;
                if (this.smsCodeTimer <= 0) {
                    clearInterval(timer);
                }
            }, 1000);
        },
        async sendEmailCode() {
            if (!this.isValidEmail(this.registerForm.email)) {
                ElMessage.warning('请输入正确的邮箱地址');
                return;
            }
            
            try {
                // 获取当前语言设置
                const language = this.$i18n.locale || 'zh-CN';
                
                const requestData = {
                    email: this.registerForm.email,
                    language: language
                };
                
                console.log('调用发送邮箱验证码API:', AUTH_API.SEND_EMAIL_CODE);
                console.log('请求参数:', requestData);
                
                // API待定，先打印到控制台
                this.startEmailTimer();
                ElMessage.success('验证码已发送');
            } catch (error) {
                console.error('发送验证码失败:', error);
                ElMessage.error('发送验证码失败，请稍后重试');
            }
        },
        startEmailTimer() {
            this.emailCodeTimer = 60;
            const timer = setInterval(() => {
                this.emailCodeTimer--;
                if (this.emailCodeTimer <= 0) {
                    clearInterval(timer);
                }
            }, 1000);
        },
        validatePhone(phone) {
            const reg = /^1[3-9]\d{9}$/;
            return reg.test(phone);
        },
        onLoginCaptchaRefresh(text) {
            console.log('登录验证码已刷新:', text);
        },
        validateLoginCaptcha() {
            if (this.$refs.loginCaptchaRef) {
                return this.$refs.loginCaptchaRef.validate(this.loginCaptcha);
            }
            return false;
        },
        refreshLoginCaptcha() {
            if (this.$refs.loginCaptchaRef) {
                this.$refs.loginCaptchaRef.refreshCaptcha();
            }
        },
        // 处理忘记密码
        handleForgotPassword() {
            ElMessage.info('请联系管理员重置密码');
        },
    }
}
</script>

<style scoped>
@import '@/monitor-console/styles/variables.css';
@import '@/monitor-console/styles/form-controls.css';

.login-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--mc-canvas);
    color: var(--mc-text-primary);
    font-family: var(--mc-font-body);
}

/* Top nav — 64px white bar */
.login-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    padding: 0 var(--mc-spacing-lg);
    background: var(--mc-canvas);
    border-bottom: 1px solid var(--mc-hairline-soft);
}

.nav-brand {
    display: flex;
    align-items: center;
    gap: var(--mc-spacing-sm);
}

.nav-brand-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--mc-ink);
    letter-spacing: -0.02em;
}

.nav-actions {
    display: flex;
    align-items: center;
    gap: var(--mc-spacing-sm);
}

/* Hero band — 7/5 split */
.login-hero {
    flex: 1;
    display: grid;
    grid-template-columns: 1fr 440px;
    gap: var(--mc-spacing-xxl);
    align-items: center;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    padding: var(--mc-spacing-section) var(--mc-spacing-lg);
}

.hero-copy {
    display: flex;
    flex-direction: column;
    gap: var(--mc-spacing-md);
}

.display-xl {
    font-family: var(--mc-font-display);
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 600;
    line-height: 1.05;
    letter-spacing: -0.04em;
    color: var(--mc-ink);
    margin: 0;
}

.hero-subtitle {
    font-size: 18px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--mc-body);
    max-width: 480px;
}

.login-panel {
    width: 100%;
    padding: var(--mc-spacing-xl);
    background: var(--mc-canvas);
    border: 1px solid var(--mc-hairline);
    border-radius: var(--mc-radius-lg);
    box-shadow: var(--mc-shadow-lg);
}

.login-methods,
.register-methods {
    display: flex;
    justify-content: stretch;
    width: 100%;
}

.register-methods .method-switch {
    width: 100%;
}

.alternate-login {
    margin-top: var(--mc-spacing-md);
    padding-top: var(--mc-spacing-md);
    border-top: 1px solid var(--mc-hairline-soft);
    text-align: center;
}

.alternate-link {
    font-size: 14px;
    font-weight: 600;
    color: var(--mc-muted);
    background: none;
    border: none;
    cursor: pointer;
}

.w-100 { width: 100%; }
.flex { display: flex; }
.justify-between { justify-content: space-between; }
.gap-2 { gap: 8px; }
.flex-1 { flex: 1; }
.mt-1 { margin-top: 4px; }
.mb-4 { margin-bottom: 16px; }

.password-strength-meter {
    margin-top: 8px;
    width: 100%;
}

.strength-info {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: var(--mc-muted);
}

.strength-feedback {
    margin: 8px 0 0 0;
    padding: 0;
    list-style: none;
}

.feedback-item {
    font-size: 12px;
    color: var(--mc-danger);
    margin-bottom: 2px;
}

.feedback-item:before {
    content: "•";
    margin-right: 5px;
}

.security-tips {
    margin-top: 20px;
    padding: var(--mc-spacing-sm) var(--mc-spacing-md);
    background: var(--mc-surface-card);
    border-radius: var(--mc-radius-lg);
    font-size: 13px;
}

.security-tips h4 {
    margin: 0 0 8px 0;
    color: var(--mc-body);
    font-size: 14px;
    font-weight: 600;
}

.security-tips ul {
    margin: 0;
    padding-left: 16px;
}

.security-tips li {
    margin-bottom: 4px;
    color: var(--mc-muted);
}

.mfa-container {
    text-align: center;
}

.mfa-header h3 {
    margin: 0 0 8px 0;
    color: var(--mc-ink);
    font-size: 18px;
    font-weight: 600;
}

.mfa-header p {
    margin: 0 0 24px 0;
    color: var(--mc-muted);
    font-size: 14px;
}

.mfa-input :deep(.el-input__inner) {
    text-align: center;
    font-size: 20px;
    letter-spacing: 4px;
}

/* Dark footer */
.login-footer {
    background: var(--mc-surface-dark);
    padding: var(--mc-spacing-xl) var(--mc-spacing-lg);
}

.footer-inner {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.footer-meta {
    display: inline-flex;
    align-items: baseline;
    gap: 10px;
}

.footer-brand {
    font-size: 14px;
    font-weight: 600;
    color: var(--mc-on-dark);
}

.footer-version {
    font-size: 13px;
    color: var(--mc-on-dark-soft);
}

@media (max-width: 960px) {
    .login-hero {
        grid-template-columns: 1fr;
        padding: var(--mc-spacing-xxl) var(--mc-spacing-lg);
    }

    .hero-copy {
        text-align: center;
        align-items: center;
    }

    .hero-subtitle {
        text-align: center;
    }
}

@media (max-width: 480px) {
    .login-panel {
        padding: var(--mc-spacing-md);
    }
}
</style>